import { useRef, useState, useEffect } from "react";
import { pageImageUrl } from "@/lib/api";
import { FIELD_TYPES } from "@/lib/fields";
import { FitPreviewText } from "@/components/FitPreviewText";
import { X, Check, PencilSimple, CopySimple, Plus, Minus, Asterisk, Warning } from "@phosphor-icons/react";

/**
 * Renders document pages as images with absolutely-positioned field overlays.
 * mode: "editor" | "signer" | "view"
 */
export default function DocumentCanvas({
  docId,
  pages = [],
  fields = [],
  recipients = [],
  mode = "view",
  selectedRecipientId,
  onDropField,
  onMoveField,
  onRemoveField,
  onResizeField,
  onConfigField,
  onDuplicateField,
  onToggleRequired,
  values = {},
  onValueChange,
  onSignatureClick,
  maxWidth = 820,
  armedType,
  onArmedPlaced,
}) {
  const pageRefs = useRef({});
  const rootRef = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const drawState = useRef(null);
  const [drawRect, setDrawRect] = useState(null); // { page, x, y, w, h } live preview
  const [guides, setGuides] = useState(null); // { page, vs: [x..], hs: [y..] } snap guides
  const [zoom, setZoom] = useState(1);
  const [previews, setPreviews] = useState({}); // editor-only sample text, never saved
  const [editingId, setEditingId] = useState(null);
  const zoomRef = useRef(1);
  const pinch = useRef(null);
  const ZMIN = 1, ZMAX = 3;

  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // Pinch-to-zoom (two fingers) on mobile. Single finger still pans via native scroll.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => {
      if (e.touches.length === 2) {
        pinch.current = { d0: dist(e.touches), z0: zoomRef.current };
        dragState.current = null; drawState.current = null; setDrawRect(null); setGuides(null);
      }
    };
    const onMoveT = (e) => {
      if (pinch.current && e.touches.length === 2) {
        e.preventDefault();
        const z = Math.min(ZMAX, Math.max(ZMIN, pinch.current.z0 * dist(e.touches) / pinch.current.d0));
        setZoom(z);
      }
    };
    const onEnd = (e) => { if (e.touches.length < 2) pinch.current = null; };
    el.addEventListener("touchstart", onStart, { passive: false });
    el.addEventListener("touchmove", onMoveT, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMoveT);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const colorFor = (rid) => recipients.find((r) => r.id === rid)?.color || "#0052FF";

  const SNAP = 0.006; // snap threshold as fraction of page

  // Alignment targets on a page: page edges/center + every other field's edges/center.
  const collectTargets = (pageIndex, excludeId) => {
    const xs = [0, 0.5, 1], ys = [0, 0.5, 1];
    fields.filter((f) => f.page === pageIndex && f.id !== excludeId).forEach((f) => {
      const c = safeCoords(f, pages[pageIndex]);
      xs.push(c.x, c.x + c.w / 2, c.x + c.w);
      ys.push(c.y, c.y + c.h / 2, c.y + c.h);
    });
    return { xs, ys };
  };

  // Snap a box's position on one axis by testing its start/center/end edges against targets.
  const snapAxis = (start, size, targets) => {
    const edges = [{ e: start, off: 0 }, { e: start + size / 2, off: size / 2 }, { e: start + size, off: size }];
    let best = null;
    for (const { e, off } of edges) {
      for (const t of targets) {
        const diff = Math.abs(e - t);
        if (diff <= SNAP && (!best || diff < best.diff)) best = { diff, pos: t - off, guide: t };
      }
    }
    return best;
  };

  // Snap a single point (used while drawing the moving corner).
  const snapPoint = (v, targets) => {
    let best = null;
    for (const t of targets) {
      const diff = Math.abs(v - t);
      if (diff <= SNAP && (!best || diff < best.diff)) best = { diff, pos: t, guide: t };
    }
    return best;
  };

  // Defensive: clamp/normalize field coords so a bad record can never push a field off-canvas.
  const safeCoords = (field, pg) => {
    let { x, y, w, h } = field;
    // Legacy/bad records stored in absolute points instead of 0-1 fractions.
    if (x > 1 || y > 1 || w > 1 || h > 1) {
      const pw = pg?.width || 1, ph = pg?.height || 1;
      x = x / pw; w = w / pw; y = y / ph; h = h / ph;
    }
    w = Math.min(Math.max(Number(w) || 0.1, 0.02), 1);
    h = Math.min(Math.max(Number(h) || 0.04, 0.01), 1);
    x = Math.min(Math.max(Number(x) || 0, 0), 1 - w);
    y = Math.min(Math.max(Number(y) || 0, 0), 1 - h);
    return { x, y, w, h };
  };

  const handlePageClick = (e, pageIndex) => {
    if (mode !== "editor" || !armedType) return;
    const rect = pageRefs.current[pageIndex].getBoundingClientRect();
    const cfg = FIELD_TYPES[armedType];
    let x = (e.clientX - rect.left) / rect.width - cfg.w / 2;
    let y = (e.clientY - rect.top) / rect.height - cfg.h / 2;
    x = Math.max(0, Math.min(1 - cfg.w, x));
    y = Math.max(0, Math.min(1 - cfg.h, y));
    onDropField?.(pageIndex, x, y, armedType);
    onArmedPlaced?.();
  };

  // Drag across the page (while a field type is armed) to draw a custom-sized box.
  const DRAG_THRESHOLD = 0.012; // fraction of page; below this we treat it as a click
  const startDraw = (e, pageIndex) => {
    if (mode !== "editor" || !armedType || pinch.current) return;
    const rect = pageRefs.current[pageIndex].getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    drawState.current = { page: pageIndex, rect, sx: px, sy: py };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onDraw = (e) => {
    const s = drawState.current;
    if (!s) return;
    let cx = Math.max(0, Math.min(1, (e.clientX - s.rect.left) / s.rect.width));
    let cy = Math.max(0, Math.min(1, (e.clientY - s.rect.top) / s.rect.height));
    const { xs, ys } = collectTargets(s.page, null);
    const sx = snapPoint(cx, xs);
    const sy = snapPoint(cy, ys);
    if (sx) cx = sx.pos;
    if (sy) cy = sy.pos;
    setGuides({ page: s.page, vs: sx ? [sx.guide] : [], hs: sy ? [sy.guide] : [] });
    setDrawRect({
      page: s.page,
      x: Math.min(s.sx, cx),
      y: Math.min(s.sy, cy),
      w: Math.abs(cx - s.sx),
      h: Math.abs(cy - s.sy),
    });
  };
  const endDraw = (e, pageIndex) => {
    const s = drawState.current;
    if (!s) { handlePageClick(e, pageIndex); return; }
    drawState.current = null;
    setGuides(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const r = drawRect;
    setDrawRect(null);
    if (!r || r.w < DRAG_THRESHOLD || r.h < DRAG_THRESHOLD) {
      // Treated as a click → default-sized placement.
      handlePageClick(e, pageIndex);
      return;
    }
    const w = Math.min(r.w, 1 - r.x);
    const h = Math.min(r.h, 1 - r.y);
    onDropField?.(pageIndex, r.x, r.y, armedType, w, h);
    onArmedPlaced?.();
  };

  const handleDrop = (e, pageIndex) => {
    if (mode !== "editor") return;
    e.preventDefault();
    const type = e.dataTransfer.getData("field-type");
    if (!type) return;
    const rect = pageRefs.current[pageIndex].getBoundingClientRect();
    const cfg = FIELD_TYPES[type];
    let x = (e.clientX - rect.left) / rect.width - cfg.w / 2;
    let y = (e.clientY - rect.top) / rect.height - cfg.h / 2;
    x = Math.max(0, Math.min(1 - cfg.w, x));
    y = Math.max(0, Math.min(1 - cfg.h, y));
    onDropField?.(pageIndex, x, y, type);
  };

  const startMove = (e, field) => {
    if (mode !== "editor" || armedType || pinch.current) return;
    e.stopPropagation();
    const rect = pageRefs.current[field.page].getBoundingClientRect();
    dragState.current = {
      field,
      rect,
      offsetX: e.clientX - (rect.left + field.x * rect.width),
      offsetY: e.clientY - (rect.top + field.y * rect.height),
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onMove = (e) => {
    const s = dragState.current;
    if (!s) return;
    let x = (e.clientX - s.offsetX - s.rect.left) / s.rect.width;
    let y = (e.clientY - s.offsetY - s.rect.top) / s.rect.height;
    x = Math.max(0, Math.min(1 - s.field.w, x));
    y = Math.max(0, Math.min(1 - s.field.h, y));
    const { xs, ys } = collectTargets(s.field.page, s.field.id);
    const sx = snapAxis(x, s.field.w, xs);
    const sy = snapAxis(y, s.field.h, ys);
    if (sx) x = Math.max(0, Math.min(1 - s.field.w, sx.pos));
    if (sy) y = Math.max(0, Math.min(1 - s.field.h, sy.pos));
    setGuides({ page: s.field.page, vs: sx ? [sx.guide] : [], hs: sy ? [sy.guide] : [] });
    onMoveField?.(s.field.id, x, y);
  };

  const endMove = (e) => {
    if (!dragState.current) return;
    dragState.current = null;
    setGuides(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const startResize = (e, field) => {
    if (mode !== "editor") return;
    e.stopPropagation();
    resizeState.current = { field, rect: pageRefs.current[field.page].getBoundingClientRect() };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onResize = (e) => {
    const s = resizeState.current;
    if (!s) return;
    let w = (e.clientX - s.rect.left) / s.rect.width - s.field.x;
    let h = (e.clientY - s.rect.top) / s.rect.height - s.field.y;
    w = Math.max(0.03, Math.min(1 - s.field.x, w));
    h = Math.max(0.015, Math.min(1 - s.field.y, h));
    onResizeField?.(s.field.id, w, h);
  };
  const endResize = (e) => {
    if (!resizeState.current) return;
    resizeState.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const renderFieldInner = (field, pg) => {
    const cfg = FIELD_TYPES[field.type];
    const Icon = cfg.icon;
    const color = colorFor(field.recipient_id);
    // Auto-fit font: scales with the field box so placeholder/label text always shows, no matter how small.
    const fit = { fontSize: "clamp(6px, 46cqmin, 13px)", lineHeight: 1.05 };
    const fitRow = { fontSize: "clamp(6px, 15cqh, 12px)", lineHeight: 1.05 };

    // Read-only value from an earlier signer (sequential order) — shown but not editable, labelled with who filled it.
    if (mode === "signer" && field.readonly) {
      const rv = field.value;
      const attribution = (
        <span data-testid={`filled-by-${field.id}`}
          className="absolute -top-3.5 left-0 z-30 max-w-[160px] truncate px-1.5 py-0.5 rounded bg-slate-700 text-white text-[9px] font-semibold leading-tight pointer-events-none shadow opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
          {field.filled_by || "Signed"}
        </span>
      );
      let inner;
      if (field.type === "signature" || field.type === "initials") {
        inner = rv ? <img src={rv} alt="signature" className="w-full h-full object-contain" /> : null;
      } else if (field.type === "checkbox") {
        inner = <div className="w-full h-full flex items-center justify-center" style={fit}>{rv ? <Check weight="bold" className="text-slate-700" /> : null}</div>;
      } else {
        inner = (
          <div data-testid={`readonly-field-${field.id}`} className="w-full h-full flex items-center px-1.5 overflow-hidden text-slate-700" style={fit}>
            <span className="truncate">{Array.isArray(rv) ? rv.join(", ") : String(rv ?? "")}</span>
          </div>
        );
      }
      return <>{attribution}{inner}</>;
    }

    if (mode === "signer") {
      const val = values[field.id];
      if (field.type === "signature" || field.type === "initials") {
        return val ? (
          <img src={val} alt="signature" className="w-full h-full object-contain" />
        ) : (
          <button
            type="button"
            onClick={() => onSignatureClick?.(field)}
            data-testid={`sign-field-${field.id}`}
            className="w-full h-full flex items-center justify-center gap-1 overflow-hidden px-1 font-semibold uppercase tracking-wide"
            style={{ color, ...fit }}
          >
            <Icon weight="bold" className="shrink-0" /> <span className="truncate">{cfg.label}</span>
          </button>
        );
      }
      if (field.type === "date") {
        return (
          <input
            type="date"
            value={val || ""}
            onChange={(e) => onValueChange?.(field.id, e.target.value)}
            data-testid={`sign-field-${field.id}`}
            className="w-full h-full bg-transparent px-1 outline-none"
            style={fit}
          />
        );
      }
      if (field.type === "checkbox") {
        return (
          <button type="button" onClick={() => onValueChange?.(field.id, !val)}
            data-testid={`sign-field-${field.id}`}
            className="w-full h-full flex items-center justify-center" style={fit}>
            {val ? <Check weight="bold" style={{ color }} /> : null}
          </button>
        );
      }
      if (field.type === "dropdown") {
        const opts = field.options || [];
        return (
          <div data-testid={`sign-field-${field.id}`} className="w-full h-full flex flex-col gap-1 p-1 overflow-hidden">
            {opts.map((o) => {
              const active = val === o;
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() => onValueChange?.(field.id, o)}
                  data-testid={`sign-option-${field.id}-${o}`}
                  className="flex-1 min-h-0 flex items-center gap-2 px-2 rounded border font-medium transition-colors"
                  style={active
                    ? { background: color, color: "#fff", borderColor: color, ...fitRow }
                    : { background: "#fff", color: "#334155", borderColor: `${color}55`, ...fitRow }}
                >
                  <span className="w-3 h-3 rounded-full border shrink-0 flex items-center justify-center"
                    style={{ borderColor: active ? "#fff" : color }}>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>
                  <span className="truncate text-left">{o}</span>
                </button>
              );
            })}
          </div>
        );
      }
      if (field.type === "radio") {
        return (
          <div data-testid={`sign-field-${field.id}`} className="w-full h-full flex flex-col justify-center gap-0.5 px-1.5 overflow-auto">
            {(field.options || []).map((o) => (
              <label key={o} className="flex items-center gap-1.5 text-navy cursor-pointer" style={fitRow}>
                <input type="radio" name={field.id} checked={val === o} onChange={() => onValueChange?.(field.id, o)} /> {o}
              </label>
            ))}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={val || ""}
          placeholder="Type here"
          onChange={(e) => onValueChange?.(field.id, e.target.value)}
          data-testid={`sign-field-${field.id}`}
          className="w-full h-full bg-transparent px-1.5 outline-none"
          style={fit}
        />
      );
    }

    // editor / view
    if (field.type === "dropdown" || field.type === "radio") {
      const opts = field.options || [];
      return (
        <div className="w-full h-full flex flex-col gap-1 p-1 overflow-hidden">
          {opts.length === 0 ? (
            <div className="flex items-center gap-1.5 px-1">
              <Icon size={13} weight="bold" style={{ color }} className="shrink-0" />
              <span className="font-semibold uppercase tracking-wide" style={{ color, ...fitRow }}>{cfg.label}</span>
            </div>
          ) : opts.map((o, idx) => (
            <div key={idx} className="flex-1 min-h-0 flex items-center gap-2 px-2 rounded border"
              style={{ borderColor: `${color}55`, color: "#334155", background: "#fff", ...fitRow }}>
              <span className="w-3 h-3 rounded-full border shrink-0" style={{ borderColor: color }} />
              <span className="truncate">{o}</span>
            </div>
          ))}
        </div>
      );
    }
    // for typed fields, preview WHERE the text will appear (matches the signer input)
    const placeholder = field.type === "date" ? "mm/dd/yyyy"
      : (field.type === "text" || field.type === "custom") ? "Type here" : null;
    if (placeholder !== null && mode === "editor" && editingId === field.id) {
      return (
        <input
          autoFocus
          value={previews[field.id] || ""}
          placeholder="Sample text (preview only)"
          onChange={(e) => setPreviews((p) => ({ ...p, [field.id]: e.target.value }))}
          onBlur={() => setEditingId(null)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur(); }}
          onPointerDown={(e) => e.stopPropagation()}
          data-testid={`preview-input-${field.id}`}
          className="w-full h-full bg-white/90 px-1.5 outline-none text-black"
          style={fit}
        />
      );
    }
    if (placeholder !== null && mode === "editor" && previews[field.id]) {
      return <FitPreviewText text={previews[field.id]} pageWidthPt={pg.width} testId={`preview-text-${field.id}`} />;
    }
    if (placeholder !== null) {
      return (
        <div className="w-full h-full flex items-center justify-between gap-1 px-1.5 overflow-hidden" style={fit}>
          <span className="truncate" style={{ color: `${color}B3` }}>{placeholder}</span>
          <Icon weight="bold" style={{ color }} className="shrink-0 opacity-50" />
        </div>
      );
    }
    return (
      <div className="w-full h-full flex items-center gap-1.5 px-1.5 overflow-hidden" style={fit}>
        <Icon weight="bold" style={{ color }} className="shrink-0" />
        <span className="font-semibold uppercase tracking-wide truncate" style={{ color }}>
          {cfg.label}
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="fixed right-3 bottom-24 z-40 flex flex-col rounded-full bg-navy/90 backdrop-blur shadow-lg overflow-hidden sm:hidden" data-testid="zoom-controls">
        <button type="button" onClick={() => setZoom((z) => Math.min(ZMAX, +(z + 0.25).toFixed(2)))}
          data-testid="zoom-in-btn" className="p-2.5 text-white active:bg-white/20">
          <Plus size={18} weight="bold" />
        </button>
        <button type="button" onClick={() => setZoom(1)} data-testid="zoom-reset-btn"
          className="px-1 py-1 text-white text-[10px] font-mono border-y border-white/20">{Math.round(zoom * 100)}%</button>
        <button type="button" onClick={() => setZoom((z) => Math.max(ZMIN, +(z - 0.25).toFixed(2)))}
          data-testid="zoom-out-btn" className="p-2.5 text-white active:bg-white/20">
          <Minus size={18} weight="bold" />
        </button>
      </div>
      <div
        ref={rootRef}
        className="flex flex-col items-center gap-8 mx-auto"
        style={{ width: `${zoom * 100}%`, touchAction: "pan-x pan-y" }}
      >
        {pages.map((pg, i) => {
          const pageFields = fields.filter((f) => f.page === i);
          return (
            <div key={i} className="w-full flex flex-col items-center">
              <div className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                Page {i + 1} / {pages.length}
              </div>
              <div
                ref={(el) => (pageRefs.current[i] = el)}
                onDragOver={(e) => mode === "editor" && e.preventDefault()}
                onDrop={(e) => handleDrop(e, i)}
                onPointerDown={(e) => startDraw(e, i)}
                onPointerMove={onDraw}
                onPointerUp={(e) => endDraw(e, i)}
                className={`relative doc-page bg-white paper-shadow ${mode === "editor" && armedType ? "cursor-crosshair select-none touch-none" : ""}`}
                style={{ width: "100%", maxWidth: maxWidth * zoom, aspectRatio: `${pg.width} / ${pg.height}` }}
              >
              <img
                src={pageImageUrl(docId, i)}
                alt={`page ${i + 1}`}
                draggable={false}
                className="absolute inset-0 w-full h-full select-none"
              />
              {drawRect && drawRect.page === i && armedType && (
                <div
                  className="absolute pointer-events-none rounded-[3px]"
                  style={{
                    left: `${drawRect.x * 100}%`,
                    top: `${drawRect.y * 100}%`,
                    width: `${drawRect.w * 100}%`,
                    height: `${drawRect.h * 100}%`,
                    background: `${colorFor(selectedRecipientId)}22`,
                    border: `1.5px dashed ${colorFor(selectedRecipientId)}`,
                  }}
                  data-testid="draw-field-preview"
                />
              )}
              {guides && guides.page === i && (
                <>
                  {guides.vs.map((gx, gi) => (
                    <div key={`v${gi}`} className="absolute top-0 bottom-0 pointer-events-none z-20"
                      style={{ left: `${gx * 100}%`, width: 0, borderLeft: "1px dashed #FF3B7B" }} />
                  ))}
                  {guides.hs.map((gy, gi) => (
                    <div key={`h${gi}`} className="absolute left-0 right-0 pointer-events-none z-20"
                      style={{ top: `${gy * 100}%`, height: 0, borderTop: "1px dashed #FF3B7B" }} />
                  ))}
                </>
              )}
              {pageFields.map((field) => {
                const isReadonly = mode === "signer" && field.readonly;
                const color = isReadonly ? "#94A3B8" : colorFor(field.recipient_id);
                const filled = mode === "signer" && (values[field.id] || isReadonly);
                const c = safeCoords(field, pg);
                const TEXTY = ["text", "custom", "date", "dropdown", "radio"];
                const tinyWarn = mode === "editor" && TEXTY.includes(field.type) && (c.h * pg.height < 12 || c.w * pg.width < 44);
                const isOptional = mode === "editor" && field.required === false;
                return (
                  <div
                    key={field.id}
                    onPointerDown={(e) => startMove(e, field)}
                    onPointerMove={onMove}
                    onPointerUp={endMove}
                    onClick={(e) => { if (!armedType) e.stopPropagation(); }}
                    onDoubleClick={(e) => {
                      if (mode !== "editor" || !["text", "custom", "date"].includes(field.type)) return;
                      e.stopPropagation();
                      setEditingId(field.id);
                    }}
                    title={mode === "editor" && ["text", "custom", "date"].includes(field.type) ? "Double-click to preview sample text" : undefined}
                    data-testid={`field-box-${field.id}`}
                    className={`absolute group flex items-center rounded-[3px] select-none transition-colors ${
                      mode === "editor" ? "tag-drag" : ""
                    } ${isReadonly ? "cursor-help outline-none" : ""}`}
                    tabIndex={isReadonly ? 0 : undefined}
                    aria-label={isReadonly ? `Filled by ${field.filled_by || "an earlier signer"}` : undefined}
                    style={{
                      left: `${c.x * 100}%`,
                      top: `${c.y * 100}%`,
                      width: `${c.w * 100}%`,
                      height: `${c.h * 100}%`,
                      containerType: "size",
                      background: isReadonly ? "#94A3B81F" : filled ? "transparent" : `${color}1A`,
                      borderTop: filled ? "none" : `1.5px ${mode === "editor" ? "solid" : "dashed"} ${color}`,
                      borderRight: filled ? "none" : `1.5px ${mode === "editor" ? "solid" : "dashed"} ${color}`,
                      borderBottom: filled ? "none" : `1.5px ${mode === "editor" ? "solid" : "dashed"} ${color}`,
                      borderLeft: isReadonly ? "none" : `4px solid ${color}`,
                    }}
                  >
                    {renderFieldInner(field, pg)}
                    {isOptional && (
                      <span data-testid={`optional-badge-${field.id}`}
                        className="absolute -top-2 left-0 z-10 px-1 rounded bg-slate-500 text-white text-[8px] font-semibold leading-tight pointer-events-none">
                        optional
                      </span>
                    )}
                    {tinyWarn && (
                      <span data-testid={`small-warning-${field.id}`}
                        title="This field is small — its text may render very tiny on the signed PDF. Make it larger for better legibility."
                        className="absolute top-0 right-0 z-10 bg-amber-400 rounded-bl-[3px] px-0.5 leading-none flex items-center">
                        <Warning size={10} weight="fill" className="text-white" />
                      </span>
                    )}
                    {mode === "editor" && (
                      <>
                        <button
                          type="button"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => onRemoveField?.(field.id)}
                          data-testid={`remove-field-${field.id}`}
                          className="absolute -top-2.5 -right-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow border border-slate-200 p-0.5"
                        >
                          <X size={12} weight="bold" className="text-slate-600" />
                        </button>
                        <button
                          type="button"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => onDuplicateField?.(field.id)}
                          data-testid={`duplicate-field-${field.id}`}
                          title="Duplicate field"
                          className="absolute -top-2.5 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow border border-slate-200 p-0.5"
                        >
                          <CopySimple size={12} weight="bold" className="text-slate-600" />
                        </button>
                        {(field.type === "dropdown" || field.type === "radio") && (
                          <button
                            type="button"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => onConfigField?.(field)}
                            data-testid={`config-field-${field.id}`}
                            className="absolute -top-2.5 -left-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow border border-slate-200 p-0.5"
                          >
                            <PencilSimple size={12} weight="bold" className="text-slate-600" />
                          </button>
                        )}
                        <button
                          type="button"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => onToggleRequired?.(field.id)}
                          data-testid={`required-field-${field.id}`}
                          title={field.required ? "Required — tap to make optional" : "Optional — tap to make required"}
                          className="absolute -bottom-2.5 -left-2.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow border border-slate-200 p-0.5"
                        >
                          <Asterisk size={12} weight="bold" className={field.required ? "text-destructive" : "text-slate-300"} />
                        </button>
                        <div
                          onPointerDown={(e) => startResize(e, field)}
                          onPointerMove={onResize}
                          onPointerUp={endResize}
                          data-testid={`resize-field-${field.id}`}
                          className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 rounded-sm bg-white shadow opacity-0 group-hover:opacity-100 transition-opacity cursor-nwse-resize touch-none"
                          style={{ border: `1.5px solid ${color}` }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
    </>
  );
}
